import { Component } from '@theme/component';

class CartDrawerUpsellComponent extends Component {
  #carousel = null;
  #resizeObserver = new ResizeObserver(() => {
    this.#syncNavigationState();
  });

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleVariantChange);
    this.addEventListener('click', this.#handleNavigationClick);
    window.addEventListener('resize', this.#syncNavigationState);
    this.#bindCarousel();
    this.#syncAllCards();
    this.#syncNavigationState();
    requestAnimationFrame(() => {
      this.#syncNavigationState();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleVariantChange);
    this.removeEventListener('click', this.#handleNavigationClick);
    window.removeEventListener('resize', this.#syncNavigationState);
    this.#unbindCarousel();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#bindCarousel();
    this.#syncAllCards();
    this.#syncNavigationState();
    requestAnimationFrame(() => {
      this.#syncNavigationState();
    });
  }

  #handleVariantChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.hasAttribute('data-upsell-variant-select')) return;

    const card = select.closest('[data-cart-drawer-upsell-card]');
    if (!(card instanceof HTMLElement)) return;

    this.#syncCard(card, select);
  };

  #handleNavigationClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-upsell-nav-prev], [data-upsell-nav-next]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const carousel = this.#carousel;
    if (!(carousel instanceof HTMLElement)) return;

    const cards = this.#getCards();
    if (cards.length < 2) return;

    const currentScrollLeft = carousel.scrollLeft;
    const targetCard = button.hasAttribute('data-upsell-nav-prev')
      ? this.#getPreviousCard(cards, currentScrollLeft)
      : this.#getNextCard(cards, currentScrollLeft);

    if (!(targetCard instanceof HTMLElement)) return;

    carousel.scrollTo({
      left: targetCard.offsetLeft,
      behavior: 'smooth',
    });
  };

  #syncAllCards() {
    for (const card of this.querySelectorAll('[data-cart-drawer-upsell-card]')) {
      if (!(card instanceof HTMLElement)) continue;

      const select = card.querySelector('[data-upsell-variant-select]');
      this.#syncCard(card, select instanceof HTMLSelectElement ? select : null);
    }
  }

  #syncCard(card, select) {
    if (!(card instanceof HTMLElement)) return;

    const variantIdInput = card.querySelector('[data-upsell-variant-id]');
    const addToCartButton = card.querySelector('add-to-cart-component button[name="add"]');
    const price = card.querySelector('[data-upsell-price]');

    if (!(variantIdInput instanceof HTMLInputElement)) return;

    if (!(select instanceof HTMLSelectElement)) {
      return;
    }

    const selectedOption = select.selectedOptions[0];
    if (!(selectedOption instanceof HTMLOptionElement)) return;

    const variantId = selectedOption.value;
    const isAvailable = selectedOption.dataset.available === 'true';
    const priceText = selectedOption.dataset.price;

    variantIdInput.value = variantId;
    variantIdInput.disabled = !isAvailable;

    if (price instanceof HTMLElement && priceText) {
      price.textContent = priceText;
    }

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !isAvailable;
    }
  }

  #bindCarousel() {
    const nextCarousel = this.querySelector('[data-upsell-carousel]');
    if (nextCarousel === this.#carousel) return;

    this.#unbindCarousel();

    if (nextCarousel instanceof HTMLElement) {
      this.#carousel = nextCarousel;
      this.#carousel.addEventListener('scroll', this.#syncNavigationState, { passive: true });
      this.#resizeObserver.observe(this.#carousel);
      for (const card of this.#getCards()) {
        this.#resizeObserver.observe(card);
      }
    }
  }

  #unbindCarousel() {
    if (!(this.#carousel instanceof HTMLElement)) return;

    this.#resizeObserver.disconnect();
    this.#carousel.removeEventListener('scroll', this.#syncNavigationState);
    this.#carousel = null;
  }

  #syncNavigationState = () => {
    const carousel = this.#carousel;
    const previousButton = this.querySelector('[data-upsell-nav-prev]');
    const nextButton = this.querySelector('[data-upsell-nav-next]');

    if (!(previousButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement) || !(carousel instanceof HTMLElement)) {
      return;
    }

    const maxScrollLeft = Math.max(carousel.scrollWidth - carousel.clientWidth, 0);
    const scrollLeft = Math.round(carousel.scrollLeft);

    previousButton.disabled = scrollLeft <= 0;
    nextButton.disabled = scrollLeft >= Math.round(maxScrollLeft);
  };

  #getCards() {
    return Array.from(this.querySelectorAll('[data-cart-drawer-upsell-card]')).filter((card) => card instanceof HTMLElement);
  }

  #getNextCard(cards, currentScrollLeft) {
    const tolerance = 4;
    return cards.find((card) => card.offsetLeft > currentScrollLeft + tolerance) ?? cards[cards.length - 1];
  }

  #getPreviousCard(cards, currentScrollLeft) {
    const tolerance = 4;
    const visibleCards = cards.filter((card) => card.offsetLeft < currentScrollLeft - tolerance);
    return visibleCards[visibleCards.length - 1] ?? cards[0];
  }
}

if (!customElements.get('cart-drawer-upsell-component')) {
  customElements.define('cart-drawer-upsell-component', CartDrawerUpsellComponent);
}
